import { useState, useEffect, useCallback } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct, getCategories } from '../../utils/api';
import ImageUploader from '../../components/common/ImageUploader';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX } from 'react-icons/fi';
import styles from './AdminTable.module.css';

const EMPTY = {
  name:'', description:'', shortDescription:'', price:'', discountPrice:'',
  category:'', brand:'', stock:'', sku:'', thumbnail:'', images:[],
  isFeatured:false, isActive:true, tags:'', warranty:'', deliveryInfo:'',
};

export default function AdminProducts() {
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [editProd, setEditProd]     = useState(null);
  const [form, setForm]             = useState(EMPTY);
  const [saving, setSaving]         = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:15 };
      if (search) params.search = search;
      const { data } = await getProducts(params);
      setProducts(data.products); setTotal(data.total);
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { getCategories().then(r => setCategories(r.data)); }, []);

  const openCreate = () => { setForm(EMPTY); setEditProd(null); setShowModal(true); };
  const openEdit   = p  => {
    setForm({
      ...p,
      price:         p.price || '',
      discountPrice: p.discountPrice || '',
      stock:         p.stock || '',
      category:      p.category?._id || p.category || '',
      tags:          p.tags?.join(', ') || '',
      images:        p.images || [],
      thumbnail:     p.thumbnail || '',
    });
    setEditProd(p); setShowModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price:         Number(form.price),
        discountPrice: form.discountPrice ? Number(form.discountPrice) : undefined,
        stock:         Number(form.stock),
        tags:          form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        images:        form.thumbnail ? [form.thumbnail, ...(form.images||[]).filter(i => i && i !== form.thumbnail)] : form.images,
      };
      if (editProd) { await updateProduct(editProd._id, payload); toast.success('✅ Product updated!'); }
      else           { await createProduct(payload);                toast.success('✅ Product created!'); }
      setShowModal(false);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await deleteProduct(id); toast.success('Deleted'); fetchProducts(); }
    catch { toast.error('Failed'); }
  };

  const f = field => e => setForm(p => ({ ...p, [field]: e.target.type==='checkbox' ? e.target.checked : e.target.value }));
  const pages = Math.ceil(total / 15);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Products</h2>
          <p className={styles.pageSubtitle}>{total} total products</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus size={15}/> Add Product</button>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch size={15} className={styles.searchIcon}/>
          <input className={styles.searchInput} placeholder="Search products…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}/>
          {search && <button className={styles.clearSearch} onClick={() => setSearch('')}><FiX size={13}/></button>}
        </div>
      </div>

      <div className={styles.tableCard}>
        {loading ? <div className="spinner-center"><div className="spinner"/></div> : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {products.length === 0
                  ? <tr><td colSpan={6} className={styles.emptyRow}>No products found</td></tr>
                  : products.map(p => (
                  <tr key={p._id}>
                    <td>
                      <div className={styles.productCell}>
                        <img src={p.thumbnail||'https://via.placeholder.com/40'} alt={p.name} className={styles.productThumb}/>
                        <div>
                          <p className={styles.productName}>{p.name}</p>
                          {p.brand && <p className={styles.productSub}>{p.brand}</p>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize:'.82rem' }}>{p.category?.name || '—'}</td>
                    <td>
                      <span style={{ fontWeight:700 }}>৳{p.price?.toLocaleString()}</span>
                      {p.discountPrice && <span style={{ display:'block', fontSize:'.72rem', color:'var(--accent)' }}>৳{p.discountPrice?.toLocaleString()}</span>}
                    </td>
                    <td><span className={`badge ${p.stock>10?'badge-success':p.stock>0?'badge-warning':'badge-error'}`}>{p.stock}</span></td>
                    <td><span className={`badge ${p.isActive?'badge-success':'badge-error'}`}>{p.isActive?'Active':'Hidden'}</span></td>
                    <td>
                      <div className={styles.actionBtns}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}><FiEdit2 size={13}/></button>
                        <button className="btn btn-ghost btn-sm" style={{ color:'var(--error)' }} onClick={() => handleDelete(p._id, p.name)}><FiTrash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="pagination">
            {Array.from({ length:pages },(_,i) => i+1).map(p => (
              <button key={p} className={`page-btn ${p===page?'active':''}`} onClick={() => setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editProd ? '✏️ Edit Product' : '➕ Add New Product'}</h3>
              <button className={styles.modalClose} onClick={() => setShowModal(false)}><FiX size={20}/></button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalForm}>
              <div className={styles.formGrid2}>

                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Product Name *</label>
                  <input className="form-input" value={form.name} onChange={f('name')} required placeholder="e.g. Samsung Galaxy A54"/>
                </div>

                {/* Thumbnail upload */}
                <div style={{ gridColumn:'1/-1' }}>
                  <ImageUploader label="Product Thumbnail *" value={form.thumbnail} onChange={url => setForm(p => ({ ...p, thumbnail: url }))}/>
                </div>

                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-input form-select" value={form.category} onChange={f('category')} required>
                    <option value="">Select category</option>
                    {categories.map(c => <option key={c._id} value={c._id}>{c.icon} {c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Brand</label>
                  <input className="form-input" value={form.brand} onChange={f('brand')} placeholder="Samsung, Walton…"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Price (৳) *</label>
                  <input type="number" className="form-input" value={form.price} onChange={f('price')} required min={0} placeholder="0"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Discount Price (৳)</label>
                  <input type="number" className="form-input" value={form.discountPrice} onChange={f('discountPrice')} min={0} placeholder="Leave blank if no discount"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Stock *</label>
                  <input type="number" className="form-input" value={form.stock} onChange={f('stock')} required min={0}/>
                </div>
                <div className="form-group">
                  <label className="form-label">SKU</label>
                  <input className="form-input" value={form.sku} onChange={f('sku')} placeholder="SKU-001"/>
                </div>

                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Short Description</label>
                  <input className="form-input" value={form.shortDescription} onChange={f('shortDescription')} placeholder="One line description…"/>
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Full Description *</label>
                  <textarea className="form-input" rows={3} value={form.description} onChange={f('description')} required placeholder="Detailed product description…"/>
                </div>

                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Tags (comma-separated)</label>
                  <input className="form-input" value={form.tags} onChange={f('tags')} placeholder="electronics, samsung, mobile phone"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Warranty</label>
                  <input className="form-input" value={form.warranty} onChange={f('warranty')} placeholder="1 Year Brand Warranty"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Info</label>
                  <input className="form-input" value={form.deliveryInfo} onChange={f('deliveryInfo')} placeholder="3–5 business days"/>
                </div>

                <div className={styles.checkboxGroup}>
                  <label className={styles.checkbox}><input type="checkbox" checked={form.isFeatured} onChange={f('isFeatured')}/> ⭐ Featured</label>
                  <label className={styles.checkbox}><input type="checkbox" checked={form.isActive} onChange={f('isActive')}/> ✅ Active (visible)</label>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><div className="spinner spinner-sm"/> Saving…</> : editProd ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
